import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ConfirmDialog from "../ConfirmDialog";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Paper,
} from "@mui/material";
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  GitHub as GitHubIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Key as KeyIcon,
  ContentCopy as ContentCopyIcon,
  RocketLaunch as RocketLaunchIcon,
  CloudUpload as CloudUploadIcon,
  Warning as WarningIcon,
  Code as CodeIcon,
} from "@mui/icons-material";
import {
  fetchGitConfigsBySite,
  createGitConfig,
  updateGitConfig,
  deleteGitConfig,
  testGitConnection,
  fetchAllSshKeys,
  generateSshKey,
  triggerDeployment,
  clearConnectionTestResult,
} from "../../redux/slices/gitSlice";
import {
  syncToGit as syncToGitService,
  checkConflicts as checkConflictsService,
  resolveConflict as resolveConflictService,
} from "../../services/Git";

const SiteGitTab = ({ siteId }) => {
  const dispatch = useDispatch();
  const {
    gitConfigs,
    gitConfigsLoading,
    sshKeys,
    sshKeysLoading,
    connectionTestResult,
    connectionTestLoading,
  } = useSelector((state) => state.git);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [sshKeyDialogOpen, setSshKeyDialogOpen] = useState(false);
  const [sshKeysViewOpen, setSshKeysViewOpen] = useState(false);
  const [newKeyResultOpen, setNewKeyResultOpen] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deployPathType, setDeployPathType] = useState("wp-content/themes");
  const [syncing, setSyncing] = useState(false);
  const [syncCommitMsg, setSyncCommitMsg] = useState("");
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [conflictDeploymentId, setConflictDeploymentId] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Form state
  const [formData, setFormData] = useState({
    provider: "GITHUB",
    repositoryUrl: "",
    branch: "main",
    authMethod: "ssh", // "ssh" or "token"
    sshKeyId: "",
    accessToken: "",
    deployPath: "wp-content/themes",
    buildCommand: "",
    excludePaths: "",
    autoDeployEnabled: false,
    environment: "SANDBOX",
  });

  const [sshKeyForm, setSSHKeyForm] = useState({
    keyName: "",
    comment: "",
  });

  useEffect(() => {
    if (siteId) {
      dispatch(fetchGitConfigsBySite(siteId));
      dispatch(fetchAllSshKeys());
    }
  }, [dispatch, siteId]);

  const handleMenuOpen = (event, config) => {
    setMenuAnchor(event.currentTarget);
    setSelectedConfig(config);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleOpenDialog = (config = null) => {
    if (config) {
      const dp = config.deployPath || "";
      const presets = [
        "",
        "wp-content",
        "wp-content/themes",
        "wp-content/plugins",
      ];
      const isPreset = presets.includes(dp);
      setDeployPathType(isPreset ? dp : "custom");
      setFormData({
        provider:
          config.gitProvider?.toUpperCase() || config.provider || "GITHUB",
        repositoryUrl: config.repositoryUrl,
        branch: config.branch,
        authMethod: config.sshKeyId ? "ssh" : "token",
        sshKeyId: config.sshKeyId || "",
        accessToken: "", // Never pre-fill token for security
        deployPath: dp,
        buildCommand: config.buildCommand || "",
        excludePaths: config.excludePaths?.join(", ") || "",
        autoDeployEnabled: config.autoDeployEnabled || false,
        environment: config.environment || "SANDBOX",
      });
      setSelectedConfig(config);
    } else {
      setDeployPathType("wp-content/themes");
      setFormData({
        provider: "GITHUB",
        repositoryUrl: "",
        branch: "main",
        authMethod: "ssh",
        sshKeyId: "",
        accessToken: "",
        deployPath: "wp-content/themes",
        buildCommand: "",
        excludePaths: "",
        autoDeployEnabled: false,
        environment: "SANDBOX",
      });
      setSelectedConfig(null);
    }
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedConfig(null);
    dispatch(clearConnectionTestResult());
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        siteId,
        gitProvider: formData.provider.toLowerCase(), // Backend expects lowercase gitProvider
        repositoryUrl: formData.repositoryUrl,
        branch: formData.branch,
        deployPath: formData.deployPath,
        buildCommand: formData.buildCommand,
        environment: formData.environment,
        autoDeployEnabled: formData.autoDeployEnabled,
        excludePaths: formData.excludePaths
          ? formData.excludePaths.split(",").map((p) => p.trim())
          : [],
        // Auth: send SSH key OR access token, not both
        ...(formData.authMethod === "ssh"
          ? { sshKeyId: formData.sshKeyId }
          : { accessToken: formData.accessToken }),
      };

      if (selectedConfig) {
        await dispatch(
          updateGitConfig({ configId: selectedConfig.id, payload }),
        ).unwrap();
      } else {
        await dispatch(createGitConfig(payload)).unwrap();
      }

      handleCloseDialog();
      dispatch(fetchGitConfigsBySite(siteId));
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to save git config: ${error.message || error}`,
        severity: "error",
      });
    }
  };

  const handleDelete = async () => {
    setDeleteConfirmOpen(false);
    if (selectedConfig) {
      try {
        await dispatch(deleteGitConfig(selectedConfig.id)).unwrap();
        dispatch(fetchGitConfigsBySite(siteId));
      } catch (error) {
        setSnackbar({
          open: true,
          message: `Failed to delete git config: ${error.message || error}`,
          severity: "error",
        });
      }
    }
    handleMenuClose();
  };

  const handleTestConnection = async (configId) => {
    setTestingConnection(configId);
    try {
      const result = await dispatch(testGitConnection(configId)).unwrap();
      // Show success feedback
      if (result.success) {
        setSnackbar({
          open: true,
          message: "Connection successful! Repository is accessible.",
          severity: "success",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Connection failed: ${
          error.message || "Unable to connect to repository"
        }`,
        severity: "error",
      });
    } finally {
      setTestingConnection(null);
    }
  };

  const handleGenerateSSHKey = async () => {
    try {
      const result = await dispatch(generateSshKey(sshKeyForm)).unwrap();
      setSshKeyDialogOpen(false);
      setSSHKeyForm({ keyName: "", comment: "" });

      // Show the generated key (with private key) once
      setGeneratedKey(result.data);
      setNewKeyResultOpen(true);

      // Refresh the keys list
      dispatch(fetchAllSshKeys());
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to generate SSH key: ${error.message}`,
        severity: "error",
      });
    }
  };

  const handleCopyPublicKey = (publicKey) => {
    navigator.clipboard.writeText(publicKey);
    setSnackbar({
      open: true,
      message: "Public key copied to clipboard!",
      severity: "success",
    });
  };

  const handleDeployNow = async () => {
    if (!selectedConfig) return;
    setDeploying(true);
    try {
      await dispatch(
        triggerDeployment({
          configId: selectedConfig.id,
          payload: { branch: selectedConfig.branch },
        }),
      ).unwrap();
      setDeployDialogOpen(false);
      setSnackbar({
        open: true,
        message:
          "Deployment triggered! Check the Deployments tab for progress.",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Deploy failed: ${error?.message || error}`,
        severity: "error",
      });
    } finally {
      setDeploying(false);
    }
  };

  const getProviderIcon = (provider) => {
    switch ((provider || "").toUpperCase()) {
      case "GITHUB":
        return <GitHubIcon />;
      case "GITLAB":
        return <CodeIcon />;
      case "BITBUCKET":
        return <CodeIcon />;
      default:
        return <GitHubIcon />;
    }
  };

  // ===== Sync to Git handlers =====
  const handleSyncToGit = async () => {
    if (!selectedConfig) return;
    setSyncing(true);
    try {
      await syncToGitService(selectedConfig.id, {
        commitMessage: syncCommitMsg || undefined,
      });
      setSyncDialogOpen(false);
      setSyncCommitMsg("");
      setSnackbar({
        open: true,
        message: "Sync to Git initiated! Changes are being pushed.",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Sync failed: ${error?.response?.data?.message || error?.message || error}`,
        severity: "error",
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleCheckConflicts = async (config) => {
    try {
      const result = await checkConflictsService(config.id);
      if (result.data?.hasConflicts) {
        setConflictData(result.data);
        setConflictDialogOpen(true);
      } else {
        setSnackbar({
          open: true,
          message: "No file conflicts detected.",
          severity: "success",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to check conflicts: ${error?.message || error}`,
        severity: "error",
      });
    }
  };

  const handleResolveConflict = async (action) => {
    if (!conflictDeploymentId) return;
    setResolving(true);
    try {
      await resolveConflictService(conflictDeploymentId, action);
      setConflictDialogOpen(false);
      setConflictData(null);
      setConflictDeploymentId(null);
      setSnackbar({
        open: true,
        message:
          action === "cancel"
            ? "Deployment cancelled."
            : action === "sync_first"
              ? "Syncing changes to Git, then deploying..."
              : "Force deploying...",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to resolve: ${error?.message || error}`,
        severity: "error",
      });
    } finally {
      setResolving(false);
    }
  };

  const getStatusColor = (isActive) => {
    return isActive ? "success" : "default";
  };

  if (gitConfigsLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <CardContent>
      {/* Header */}
      <Box mb={3}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="h6">Git Integration</Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<KeyIcon />}
              onClick={() => setSshKeysViewOpen(true)}
              sx={{ mr: 1 }}
            >
              SSH Keys ({sshKeys?.length || 0})
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Repository
            </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Connect your Git repository to deploy code automatically. Start by
          generating an SSH key, then add your repository.
        </Typography>
      </Box>

      {/* Git Configurations List */}
      <Grid container spacing={2}>
        {gitConfigs && gitConfigs.length > 0 ? (
          gitConfigs.map((config) => (
            <Grid size={{ xs: 12 }} key={config.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={2}>
                      {getProviderIcon(config.gitProvider || config.provider)}
                      <Box>
                        <Typography variant="h6">
                          {config.repositoryUrl
                            .split("/")
                            .pop()
                            .replace(".git", "")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {config.repositoryUrl}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={config.branch}
                        size="small"
                        color="primary"
                      />
                      <Chip
                        label={config.environment}
                        size="small"
                        color={getStatusColor(config.isActive)}
                      />
                      {config.autoDeployEnabled && (
                        <Chip label="Auto-Deploy" size="small" color="info" />
                      )}
                      {connectionTestResult?.configId === config.id && (
                        <Chip
                          label={
                            connectionTestResult.success
                              ? "✓ Connected"
                              : "✗ Failed"
                          }
                          size="small"
                          color={
                            connectionTestResult.success ? "success" : "error"
                          }
                          variant="outlined"
                        />
                      )}
                      <Tooltip title="Test Connection">
                        <IconButton
                          size="small"
                          onClick={() => handleTestConnection(config.id)}
                          disabled={testingConnection === config.id}
                        >
                          {testingConnection === config.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <RefreshIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, config)}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box mt={2}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                          Deploy Path
                        </Typography>
                        <Typography variant="body2" fontFamily="monospace">
                          /var/www/html/{config.deployPath || ""}
                        </Typography>
                        {!config.deployPath && (
                          <Chip
                            label="Full Site"
                            size="small"
                            color="warning"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                          Build Command
                        </Typography>
                        <Typography variant="body2">
                          {config.buildCommand || "None"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                          Last Used
                        </Typography>
                        <Typography variant="body2">
                          {config.lastUsedAt
                            ? new Date(config.lastUsedAt).toLocaleString()
                            : "Never"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                          Last Sync
                        </Typography>
                        <Typography variant="body2">
                          {config.lastSyncAt
                            ? new Date(config.lastSyncAt).toLocaleString()
                            : "Never synced"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                          Created
                        </Typography>
                        <Typography variant="body2">
                          {new Date(config.createdAt).toLocaleDateString()}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Webhook Setup Info */}
                  {config.autoDeployEnabled && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        Webhook Setup
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1 }}
                      >
                        Add this URL as a webhook in your{" "}
                        {config.gitProvider || "Git"} repository settings to
                        enable auto-deploy on push:
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            bgcolor: "background.paper",
                            p: 0.5,
                            px: 1,
                            borderRadius: 0.5,
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {`${process.env.REACT_APP_API_BASE || ""}/git/webhooks/${(config.gitProvider || "github").toLowerCase()}/${config.id}`}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              `${process.env.REACT_APP_API_BASE || ""}/git/webhooks/${(config.gitProvider || "github").toLowerCase()}/${config.id}`,
                            );
                            setSnackbar({
                              open: true,
                              message: "Webhook URL copied!",
                              severity: "success",
                            });
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      {config.webhookSecret && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Secret:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              bgcolor: "background.paper",
                              p: 0.5,
                              px: 1,
                              borderRadius: 0.5,
                              fontSize: "0.75rem",
                            }}
                          >
                            {config.webhookSecret}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                config.webhookSecret,
                              );
                              setSnackbar({
                                open: true,
                                message: "Webhook secret copied!",
                                severity: "success",
                              });
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 1 }}
                      >
                        Content type: application/json • Events: Push
                      </Typography>
                    </Box>
                  )}

                  {connectionTestResult && selectedConfig?.id === config.id && (
                    <Alert
                      severity={
                        connectionTestResult.success ? "success" : "error"
                      }
                      sx={{ mt: 2 }}
                      onClose={() => dispatch(clearConnectionTestResult())}
                    >
                      {connectionTestResult.message ||
                        connectionTestResult.error}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid size={{ xs: 12 }}>
            <Card sx={{ bgcolor: "background.paper" }}>
              <CardContent sx={{ textAlign: "center", py: 6 }}>
                <GitHubIcon
                  sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                />
                <Typography variant="h6" gutterBottom>
                  No Git Repositories Connected
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3, maxWidth: 600, mx: "auto" }}
                >
                  Connect your GitHub, GitLab, or Bitbucket repository to deploy
                  code changes automatically. You'll need to generate an SSH key
                  first and add it to your Git provider.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<KeyIcon />}
                    onClick={() => setSshKeyDialogOpen(true)}
                  >
                    1. Generate SSH Key
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                  >
                    2. Add Repository
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Config Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleOpenDialog(selectedConfig)}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setDeployDialogOpen(true);
          }}
        >
          <RocketLaunchIcon fontSize="small" sx={{ mr: 1 }} />
          Deploy Now
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setSyncDialogOpen(true);
          }}
        >
          <CloudUploadIcon fontSize="small" sx={{ mr: 1 }} />
          Sync to Git
        </MenuItem>
        <MenuItem
          onClick={() => handleTestConnection(selectedConfig?.id)}
          disabled={connectionTestLoading}
        >
          <RefreshIcon fontSize="small" sx={{ mr: 1 }} />
          Test Connection
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setDeleteConfirmOpen(true);
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Deploy Confirmation Dialog */}
      <Dialog
        open={deployDialogOpen}
        onClose={() => !deploying && setDeployDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <RocketLaunchIcon color="primary" />
            <Typography variant="h6">Deploy from Git</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedConfig && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                This will pull the latest code from your repository and deploy
                it to your WordPress site.
              </Alert>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Repository
                </Typography>
                <Typography variant="body1" fontFamily="monospace">
                  {selectedConfig.repositoryUrl}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Branch
                </Typography>
                <Chip
                  label={selectedConfig.branch || "main"}
                  color="primary"
                  size="small"
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Deploy Path
                </Typography>
                <Typography variant="body1" fontFamily="monospace">
                  {selectedConfig.deployPath
                    ? `/var/www/html/${selectedConfig.deployPath}`
                    : "/var/www/html/ (full site)"}
                </Typography>
              </Box>
              {selectedConfig.buildCommand && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Build Command
                  </Typography>
                  <Typography variant="body1" fontFamily="monospace">
                    {selectedConfig.buildCommand}
                  </Typography>
                </Box>
              )}
              {(!selectedConfig.deployPath ||
                selectedConfig.deployPath === "/") && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <strong>Full site deployment!</strong> This will overwrite all
                  files in the WordPress root directory. Make sure your repo
                  contains a complete WordPress installation.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeployDialogOpen(false)}
            disabled={deploying}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeployNow}
            variant="contained"
            color="primary"
            disabled={deploying}
            startIcon={
              deploying ? <CircularProgress size={16} /> : <RocketLaunchIcon />
            }
          >
            {deploying ? "Deploying..." : "Deploy Now"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sync to Git Dialog */}
      <Dialog
        open={syncDialogOpen}
        onClose={() => !syncing && setSyncDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CloudUploadIcon color="primary" />
            <Typography variant="h6">Sync to Git</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Push local changes (SFTP edits, plugin installs, theme changes) back
            to your Git repository. Sensitive files (wp-config.php, .env,
            uploads) are excluded automatically.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Your SSH deploy key must have <strong>write access</strong> on your
            repository for this to work.
          </Alert>
          <TextField
            fullWidth
            label="Commit Message (optional)"
            placeholder="WP Spark sync: local changes from SFTP/WP-Admin"
            value={syncCommitMsg}
            onChange={(e) => setSyncCommitMsg(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)} disabled={syncing}>
            Cancel
          </Button>
          <Button
            onClick={handleSyncToGit}
            variant="contained"
            color="primary"
            disabled={syncing}
            startIcon={
              syncing ? <CircularProgress size={16} /> : <CloudUploadIcon />
            }
          >
            {syncing ? "Syncing..." : "Push to Git"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conflict Resolution Dialog */}
      <Dialog
        open={conflictDialogOpen}
        onClose={() => !resolving && setConflictDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            <Typography variant="h6">File Conflicts Detected</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {conflictData && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {conflictData.totalChanges} file(s) have been modified locally
                since the last Git deploy. Deploying will overwrite these
                changes.
              </Alert>
              {conflictData.modified?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Modified ({conflictData.modified.length})
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ maxHeight: 150, overflow: "auto", p: 1 }}
                  >
                    {conflictData.modified.map((f) => (
                      <Typography
                        key={f}
                        variant="body2"
                        fontFamily="monospace"
                        fontSize="0.75rem"
                      >
                        {f}
                      </Typography>
                    ))}
                  </Paper>
                </Box>
              )}
              {conflictData.added?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Added ({conflictData.added.length})
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ maxHeight: 150, overflow: "auto", p: 1 }}
                  >
                    {conflictData.added.map((f) => (
                      <Typography
                        key={f}
                        variant="body2"
                        fontFamily="monospace"
                        fontSize="0.75rem"
                        color="success.main"
                      >
                        + {f}
                      </Typography>
                    ))}
                  </Paper>
                </Box>
              )}
              {conflictData.deleted?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Deleted ({conflictData.deleted.length})
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ maxHeight: 150, overflow: "auto", p: 1 }}
                  >
                    {conflictData.deleted.map((f) => (
                      <Typography
                        key={f}
                        variant="body2"
                        fontFamily="monospace"
                        fontSize="0.75rem"
                        color="error.main"
                      >
                        - {f}
                      </Typography>
                    ))}
                  </Paper>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1, p: 2 }}>
          <Button
            onClick={() => handleResolveConflict("cancel")}
            disabled={resolving}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleResolveConflict("force_deploy")}
            variant="outlined"
            color="error"
            disabled={resolving}
          >
            Deploy Anyway (Overwrite)
          </Button>
          <Button
            onClick={() => handleResolveConflict("sync_first")}
            variant="contained"
            color="primary"
            disabled={resolving}
            startIcon={
              resolving ? <CircularProgress size={16} /> : <CloudUploadIcon />
            }
          >
            Push Changes First & Deploy
          </Button>
        </DialogActions>
      </Dialog>

      {/* Config Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedConfig ? "Edit" : "Add"} Git Configuration
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            {/* Repository Settings */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Repository Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Provider</InputLabel>
                <Select
                  value={formData.provider}
                  onChange={(e) => handleFormChange("provider", e.target.value)}
                  label="Provider"
                >
                  <MenuItem value="GITHUB">
                    <Box display="flex" alignItems="center" gap={1}>
                      <GitHubIcon fontSize="small" />
                      GitHub
                    </Box>
                  </MenuItem>
                  <MenuItem value="GITLAB">GitLab</MenuItem>
                  <MenuItem value="BITBUCKET">Bitbucket</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Authentication</InputLabel>
                <Select
                  value={formData.authMethod}
                  onChange={(e) =>
                    handleFormChange("authMethod", e.target.value)
                  }
                  label="Authentication"
                >
                  <MenuItem value="ssh">SSH Key (Deploy Key)</MenuItem>
                  <MenuItem value="token">HTTPS + Access Token (PAT)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.authMethod === "ssh" ? (
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>SSH Key</InputLabel>
                  <Select
                    value={formData.sshKeyId}
                    onChange={(e) =>
                      handleFormChange("sshKeyId", e.target.value)
                    }
                    label="SSH Key"
                  >
                    {sshKeys?.map((key) => (
                      <MenuItem key={key.id} value={key.id}>
                        {key.keyName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Personal Access Token"
                  type="password"
                  value={formData.accessToken}
                  onChange={(e) =>
                    handleFormChange("accessToken", e.target.value)
                  }
                  placeholder="ghp_xxxx / glpat-xxxx"
                  helperText="GitHub PAT, GitLab token, or Bitbucket app password"
                />
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Repository URL"
                value={formData.repositoryUrl}
                onChange={(e) =>
                  handleFormChange("repositoryUrl", e.target.value)
                }
                placeholder={
                  formData.authMethod === "ssh"
                    ? "git@github.com:username/repository.git"
                    : "https://github.com/username/repository.git"
                }
                helperText={
                  formData.authMethod === "ssh"
                    ? "SSH URL of your Git repository (e.g., git@github.com:user/repo.git)"
                    : "HTTPS URL of your Git repository (e.g., https://github.com/user/repo.git)"
                }
              />
            </Grid>

            {/* Branch & Environment */}
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="subtitle2"
                color="primary"
                gutterBottom
                sx={{ mt: 1 }}
              >
                Branch & Environment
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Branch"
                value={formData.branch}
                onChange={(e) => handleFormChange("branch", e.target.value)}
                placeholder="main"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Environment</InputLabel>
                <Select
                  value={formData.environment}
                  onChange={(e) =>
                    handleFormChange("environment", e.target.value)
                  }
                  label="Environment"
                >
                  <MenuItem value="SANDBOX">Sandbox</MenuItem>
                  <MenuItem value="STAGING">Staging</MenuItem>
                  <MenuItem value="PRODUCTION">Production</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Deployment Settings */}
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="subtitle2"
                color="primary"
                gutterBottom
                sx={{ mt: 1 }}
              >
                Deployment Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Deploy Path</InputLabel>
                <Select
                  value={deployPathType}
                  label="Deploy Path"
                  onChange={(e) => {
                    const val = e.target.value;
                    setDeployPathType(val);
                    if (val !== "custom") {
                      handleFormChange("deployPath", val);
                    }
                  }}
                >
                  <MenuItem value="">
                    <Box>
                      <Typography variant="body2">
                        / (Full WordPress Root)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Repo root contains wp-content/ folder (or full WP
                        install)
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="wp-content">
                    <Box>
                      <Typography variant="body2">wp-content/</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Repo root has themes/, plugins/ folders (NOT inside
                        wp-content/)
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="wp-content/themes">
                    <Box>
                      <Typography variant="body2">
                        wp-content/themes/
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Repo root has theme folders like twentytwentyfive/,
                        mytheme/
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="wp-content/plugins">
                    <Box>
                      <Typography variant="body2">
                        wp-content/plugins/
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Repo root has plugin folders like akismet/, myplugin/
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="custom">
                    <Typography variant="body2">Custom path...</Typography>
                  </MenuItem>
                </Select>
              </FormControl>
              {deployPathType === "custom" && (
                <TextField
                  fullWidth
                  label="Custom Deploy Path"
                  value={formData.deployPath}
                  onChange={(e) =>
                    handleFormChange("deployPath", e.target.value)
                  }
                  placeholder="wp-content/themes/my-theme"
                  helperText="Relative path inside /var/www/html/"
                  sx={{ mt: 1 }}
                />
              )}
              <Alert severity="info" variant="outlined" sx={{ mt: 1, py: 0 }}>
                <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                  <strong>How deployment works:</strong> All files from your
                  repo root are copied into the deploy path.
                </Typography>
                <Typography variant="caption" component="div">
                  ▸ Repo has <code>style.css</code> at root + pick{" "}
                  <code>wp-content/themes/</code>→ file lands at{" "}
                  <code>/var/www/html/wp-content/themes/style.css</code>
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ mt: 0.5, color: "error.main" }}
                >
                  ▸ Repo has <code>wp-content/</code> folder at root + pick{" "}
                  <code>wp-content/</code>→ incorrect, nested to{" "}
                  <code>/var/www/html/wp-content/wp-content/</code>
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ mt: 0.5, color: "success.main" }}
                >
                  ✓ Repo has <code>wp-content/</code> folder at root → pick{" "}
                  <code>/ (Full WordPress Root)</code>
                </Typography>
              </Alert>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Build Command"
                value={formData.buildCommand}
                onChange={(e) =>
                  handleFormChange("buildCommand", e.target.value)
                }
                placeholder="npm install && npm run build"
                helperText="Optional build command to run before deployment"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Exclude Paths"
                value={formData.excludePaths}
                onChange={(e) =>
                  handleFormChange("excludePaths", e.target.value)
                }
                placeholder="node_modules, .git, tests"
                helperText="Comma-separated paths to exclude from deployment"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.autoDeployEnabled}
                    onChange={(e) =>
                      handleFormChange("autoDeployEnabled", e.target.checked)
                    }
                  />
                }
                label="Enable Auto-Deploy (via webhooks)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedConfig ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SSH Key Dialog */}
      <Dialog
        open={sshKeyDialogOpen}
        onClose={() => setSshKeyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate SSH Key</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            After generating the key, you'll receive a public key. Copy it and
            add it to your Git provider's SSH keys settings (GitHub → Settings →
            SSH Keys).
          </Alert>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Key Name"
                value={sshKeyForm.keyName}
                onChange={(e) =>
                  setSSHKeyForm((prev) => ({
                    ...prev,
                    keyName: e.target.value,
                  }))
                }
                placeholder="my-deploy-key"
                helperText="Alphanumeric characters, dashes, and underscores only"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Comment (Optional)"
                value={sshKeyForm.comment}
                onChange={(e) =>
                  setSSHKeyForm((prev) => ({
                    ...prev,
                    comment: e.target.value,
                  }))
                }
                placeholder="Deploy key for production"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSshKeyDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleGenerateSSHKey}
            variant="contained"
            disabled={!sshKeyForm.keyName || sshKeysLoading}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      {/* New SSH Key Result Dialog - Shows private key ONCE */}
      <Dialog
        open={newKeyResultOpen}
        onClose={() => {
          setNewKeyResultOpen(false);
          setGeneratedKey(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <KeyIcon color="success" />
            <Typography variant="h6">SSH Key Generated Successfully</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>Save your private key now!</strong> This is the only time
            you'll see it. Store it securely - you'll need it to authenticate
            with your Git repository.
          </Alert>

          {generatedKey && (
            <Box>
              {/* Public Key */}
              <Box mb={3}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    Public Key
                  </Typography>
                  <Tooltip title="Copy Public Key">
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(generatedKey.publicKey);
                        setSnackbar({
                          open: true,
                          message: "Public key copied!",
                          severity: "success",
                        });
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: "action.hover",
                    color: "text.primary",
                    fontFamily: "monospace",
                    fontSize: "0.75rem",
                    wordBreak: "break-all",
                    maxHeight: "150px",
                    overflow: "auto",
                  }}
                >
                  {generatedKey.publicKey}
                </Paper>
                <Typography variant="caption" color="text.secondary" mt={1}>
                  Add this to your Git provider (GitHub → Settings → SSH Keys)
                </Typography>
              </Box>

              {/* Private Key */}
              <Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="error"
                  >
                    Private Key (Save Now!)
                  </Typography>
                  <Tooltip title="Copy Private Key">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        navigator.clipboard.writeText(generatedKey.privateKey);
                        setSnackbar({
                          open: true,
                          message: "Private key copied!",
                          severity: "success",
                        });
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: "error.dark",
                    border: "2px solid",
                    borderColor: "error.main",
                    color: "error.contrastText",
                    fontFamily: "monospace",
                    fontSize: "0.75rem",
                    wordBreak: "break-all",
                    maxHeight: "250px",
                    overflow: "auto",
                  }}
                >
                  {generatedKey.privateKey}
                </Paper>
                <Typography variant="caption" color="error" mt={1}>
                  Store this securely - you won't be able to retrieve it again!
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setNewKeyResultOpen(false);
              setGeneratedKey(null);
            }}
            variant="contained"
          >
            I've Saved My Keys
          </Button>
        </DialogActions>
      </Dialog>

      {/* SSH Keys View Dialog */}
      <Dialog
        open={sshKeysViewOpen}
        onClose={() => setSshKeysViewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">SSH Keys</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSshKeysViewOpen(false);
                setSshKeyDialogOpen(true);
              }}
              size="small"
            >
              Generate New
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {sshKeys && sshKeys.length > 0 ? (
            <List>
              {sshKeys.map((key, index) => (
                <React.Fragment key={key.id}>
                  {index > 0 && <Divider />}
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {key.keyName}
                          </Typography>
                          <Chip
                            label={key.fingerprint}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box mt={1}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Created:{" "}
                            {new Date(key.createdAt).toLocaleDateString()}
                          </Typography>
                          <Paper
                            variant="outlined"
                            sx={{
                              mt: 1,
                              p: 1,
                              bgcolor: "action.hover",
                              color: "text.primary",
                              fontFamily: "monospace",
                              fontSize: "0.75rem",
                              wordBreak: "break-all",
                            }}
                          >
                            {key.publicKey}
                          </Paper>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Copy Public Key">
                        <IconButton
                          edge="end"
                          onClick={() => handleCopyPublicKey(key.publicKey)}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Alert severity="info">
              No SSH keys yet. Generate one to connect your Git repository.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSshKeysViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete Git Configuration"
        message="Are you sure you want to delete this Git configuration? This action cannot be undone."
        confirmText="Delete"
        confirmColor="error"
      />
    </CardContent>
  );
};

export default SiteGitTab;
